import React, { useState, useEffect, useRef } from 'react';
import {
    getAuth,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    RecaptchaVerifier,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    getMultiFactorResolver
} from 'firebase/auth';
import { useTheme } from '../contexts/ThemeContext';
import MfaModal from '../components/MfaModal';
import { showToast } from '../utils/toast';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // Estado para o alerta de erro de login
    const [loginError, setLoginError] = useState('');

    // Estados para o modal de MFA
    const [showMfaModal, setShowMfaModal] = useState(false);
    const [mfaResolver, setMfaResolver] = useState(null);
    const [mfaVerificationId, setMfaVerificationId] = useState('');
    const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);

    // Estados para o novo modal de recuperação de senha
    const [showResetModal, setShowResetModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);

    const { theme } = useTheme();
    const logoSrc = theme === 'dark' ? '/logo_dark.png' : '/logo_light.png';
    
    const recaptchaVerifierRef = useRef(null);
    const auth = getAuth();

    // useEffect para o reCAPTCHA
    useEffect(() => {
        if (!recaptchaVerifierRef.current) {
            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container-login', {
                'size': 'invisible'
            });
        }
        return () => {
            if (recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current.clear();
            }
        };
    }, [auth]);

    // *** AJUSTE FINAL ADICIONADO AQUI ***
    // useEffect para verificar se houve um logout forçado (ex: conta desabilitada)
    useEffect(() => {
        const reason = sessionStorage.getItem('logoutReason');
        if (reason === 'disabled') {
            setLoginError('Sua conta foi desabilitada por um administrador.');
            // Limpa o item para que a mensagem não apareça novamente se o usuário recarregar a página
            sessionStorage.removeItem('logoutReason');
        }
    }, []); // O array vazio [] garante que isso só rode uma vez, quando a página carrega.


    // Funções para o modal de recuperação de senha
    const openResetModal = () => {
        setResetEmail(email);
        setResetSuccess(false);
        setShowResetModal(true);
    };
    const closeResetModal = () => setShowResetModal(false);

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        if (!resetEmail) return;
        setIsLoading(true);
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(true);
        } catch (error) {
            console.error("Erro ao enviar e-mail de recuperação:", error);
            setResetSuccess(true);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleMfaSubmit = async (verificationCode) => {
        setIsVerifyingMfa(true);
        try {
            const cred = PhoneAuthProvider.credential(mfaVerificationId, verificationCode);
            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
            await mfaResolver.resolveSignIn(multiFactorAssertion);
            setShowMfaModal(false);
        } catch (error) {
            console.error("Código MFA inválido:", error);
            showToast('error', 'O código de verificação está incorreto. Tente novamente.');
        } finally {
            setIsLoading(false);
            setIsVerifyingMfa(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setLoginError(''); // Limpa erros anteriores

        try {
            await signInWithEmailAndPassword(auth, email, password);
            setIsLoading(false);
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, error);
                const phoneAuthProvider = new PhoneAuthProvider(auth);
                try {
                    const recaptchaVerifier = recaptchaVerifierRef.current;
                    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
                        { multiFactorHint: resolver.hints[0], session: resolver.session },
                        recaptchaVerifier
                    );
                    setMfaResolver(resolver);
                    setMfaVerificationId(verificationId);
                    setShowMfaModal(true);
                } catch (mfaError) {
                    console.error("Erro ao enviar SMS:", mfaError);
                    setLoginError('Não foi possível enviar o código de verificação. Tente novamente mais tarde.');
                    setIsLoading(false);
                }
            } else {
                let errorMessage = 'E-mail ou senha inválidos.';
                if (error.code === 'auth/user-disabled') {
                    errorMessage = 'Sua conta está desabilitada.';
                } else if (error.code === 'auth/invalid-credential') {
                    errorMessage = 'As credenciais fornecidas são inválidas.';
                }
                setLoginError(errorMessage);
                setIsLoading(false);
            }
        }
    };

    return (
        <>
            <div className="d-flex align-items-center justify-content-center vh-100 bg-body-tertiary">
                <div id="recaptcha-container-login"></div>
                
                <div className="card shadow-sm" style={{ width: '100%', maxWidth: '400px' }}>
                    <div className="card-body p-5">
                        <div className="text-center mb-4">
                            <img src={logoSrc} alt="Logo da Empresa" style={{ maxHeight: '80px' }} />
                        </div>
                        <h2 className="card-title text-center mb-4 h4">Acessar Portal</h2>
                        
                        {loginError && (
                            <Alert variant="danger" onClose={() => setLoginError('')} dismissible>
                                {loginError}
                            </Alert>
                        )}

                        <form onSubmit={handleLogin}>
                            <div className="form-floating mb-3">
                                <input
                                    type="email"
                                    className="form-control"
                                    id="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Seu e-mail"
                                    required
                                />
                                <label htmlFor="email">Seu e-mail</label>
                            </div>
                            <div className="form-floating mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    id="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Sua senha"
                                    required
                                />
                                <label htmlFor="password">Sua senha</label>
                            </div>
                            <div className="d-grid mt-4">
                                <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading}>
                                    {isLoading ? 'Aguarde...' : 'Entrar'}
                                </button>
                            </div>
                            <div className="text-center mt-3">
                                <button
                                    type="button"
                                    className="btn btn-link text-secondary"
                                    onClick={openResetModal}
                                    disabled={isLoading}
                                    style={{textDecoration: 'none'}}
                                >
                                    Esqueceu sua senha?
                                </button>
                            </div>
                            <div className="text-center mt-3" style={{ fontSize: '0.75rem', color: 'grey' }}>
                                {/* ... links de privacidade do Google ... */}
                            </div>
                        </form>
                    </div>
                </div>

                {mfaResolver && (
                    <MfaModal 
                        show={showMfaModal}
                        onHide={() => setShowMfaModal(false)}
                        onSubmit={handleMfaSubmit}
                        phoneNumberHint={mfaResolver.hints[0].phoneNumber.slice(-4)}
                        isVerifying={isVerifyingMfa}
                    />
                )}
            </div>

            <Modal show={showResetModal} onHide={closeResetModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Recuperar Senha</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {resetSuccess ? (
                        <Alert variant="success">
                            Se uma conta existir para este e-mail, um link de redefinição de senha foi enviado. Por favor, verifique sua caixa de entrada e spam.
                        </Alert>
                    ) : (
                        <Form onSubmit={handleResetSubmit}>
                            <Form.Group controlId="resetEmail">
                                <Form.Label>Endereço de e-mail</Form.Label>
                                <Form.Control
                                    type="email"
                                    placeholder="Digite o e-mail da sua conta"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    required
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeResetModal}>
                        {resetSuccess ? 'Fechar' : 'Cancelar'}
                    </Button>
                    {!resetSuccess && (
                        <Button variant="primary" form="resetForm" onClick={handleResetSubmit} disabled={isLoading}>
                            {isLoading ? 'Enviando...' : 'Enviar Link'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default LoginPage;